import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Button } from "@/components/ui/button";

// Ogni quanto chiedere al browser se c'è un nuovo service worker (= nuovo deploy).
// Con HashRouter non ci sono navigazioni vere, quindi senza questo controllo chi
// tiene la scheda aperta resterebbe sulla versione vecchia.
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

// Solo il percorso della route: un cambio di query (?tab=...) resta sulla stessa pagina
const currentRoutePath = () => window.location.hash.split("?")[0];

/**
 * Quando è disponibile una nuova versione dell'app la applica da sola al primo
 * cambio di pagina, quando l'utente sta già lasciando quello che faceva. Finché
 * resta sulla stessa pagina vede un banner fisso per ricaricare subito: non è un
 * toast, quindi gli altri messaggi non possono rimpiazzarlo.
 */
const UpdatePrompt = () => {
  const { t } = useTranslation();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      const checkForUpdate = () => {
        if (registration.installing || !navigator.onLine) return;
        registration.update().catch(() => {
          // Rete assente o sw.js non raggiungibile: si riprova al prossimo giro
        });
      };

      setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") checkForUpdate();
      });
    },
  });

  // La pagina su cui si trovava l'utente quando è arrivato l'aggiornamento
  const routeAtUpdate = useRef<string | null>(null);

  useEffect(() => {
    if (!needRefresh) return;

    routeAtUpdate.current = currentRoutePath();
    const applyOnNavigation = () => {
      if (currentRoutePath() !== routeAtUpdate.current) updateServiceWorker(true);
    };

    window.addEventListener("hashchange", applyOnNavigation);
    return () => window.removeEventListener("hashchange", applyOnNavigation);
  }, [needRefresh, updateServiceWorker]);

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-[100] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center gap-3 rounded-lg border bg-background p-3 shadow-lg"
    >
      <RefreshCw className="h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{t("appUpdate.title")}</p>
        <p className="text-xs text-muted-foreground">{t("appUpdate.description")}</p>
      </div>
      <Button size="sm" onClick={() => updateServiceWorker(true)}>
        {t("appUpdate.reload")}
      </Button>
    </div>
  );
};

export default UpdatePrompt;
